import React, { useState } from 'react';
import {
    Button,
    Col,
    Divider,
    Input,
    Option,
    Row,
    Select,
    Text,
} from '@servahealth/serva-ui-kit';
import { CoreType, CoreDataPoint, CoreDataPointDataType } from '../Shared/CoreComponents';
import { AHRBDataPointAggregateConfiguration } from '../../Interfaces/Reports/AHRBDataPointConfigurationModels';

export interface AHRBCoreDataPointAggregateProps {
    coreTypes: CoreType[];
    coreDataPoints: CoreDataPoint[];
    ahrbsDataPointID: number | undefined;
    selectedCoreDataPointColumnAggregate: AHRBDataPointAggregateConfiguration | undefined;
    onOk: (
        dataPointAggregate: AHRBDataPointAggregateConfiguration
    ) => void;
    onCancel: () => void;
}

export const AHRBCoreDataPointAggregate: React.FC<AHRBCoreDataPointAggregateProps> = ({ ...props }) => {

    const [selectedCoreDataPointAggregate, setSelectedCoreDataPointAggregate] = useState<string>(
        props.selectedCoreDataPointColumnAggregate?.aggregate ?? ''
    );

    const [selectedCoreDataPointAggregateAlias, setSelectedCoreDataPointAggregateAlias] = useState<string>(
        props.selectedCoreDataPointColumnAggregate?.aggregateColumnAlias ?? ''
    );

    const [changeOccurred, setChangeOccurred] = useState(false);
    
    const selectedDataPoint = props.coreDataPoints.find(cdp => cdp.id === props.ahrbsDataPointID);
    const selectedFieldName = selectedDataPoint?.name;
    const selectedFieldTypeID = selectedDataPoint?.idType;
    const selectedFieldDataType = selectedDataPoint?.dataType;
    const selectedTypeName = props.coreTypes.find(ct => ct.id === selectedFieldTypeID)?.name;

    const handleChange = (e: string, typeOfChange: string) => {
        setChangeOccurred(true);

        if (typeOfChange === 'aggregate') {
            setSelectedCoreDataPointAggregate(e);
        }
        else if (typeOfChange === 'aggregateColumnAlias') {
            setSelectedCoreDataPointAggregateAlias(e);
        }
    };

    const handleAggregateChange = (e: string) => {
        handleChange(e, 'aggregate');
    }

    const handleAggregateColumnAliasChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleChange(e.target.value, 'aggregateColumnAlias');
    }

    const handleOk = () => {
        props.onOk({
            aggregate: selectedCoreDataPointAggregate,
            aggregateColumnAlias: selectedCoreDataPointAggregateAlias
        });
    };

    const getAvailableAggregateOptions = (dataType: CoreDataPointDataType | undefined) => {
        switch (dataType) {
            case CoreDataPointDataType.Integer:
            case CoreDataPointDataType.Float:
            case CoreDataPointDataType.Autonumber:
                return [
                    { value: 'AVG', label: 'Average' },
                    { value: 'COUNT', label: 'Count' },
                    { value: 'MAX', label: 'Maximum' },
                    { value: 'MIN', label: 'Minimum' },
                    { value: 'SUM', label: 'Summation' },
                ];

            case CoreDataPointDataType.Date:
            case CoreDataPointDataType.DateTime:
                return [
                    { value: 'COUNT', label: 'Count' },
                    { value: 'MAX', label: 'Maximum' },
                    { value: 'MIN', label: 'Minimum' },
                ];

            case CoreDataPointDataType.String:
            case CoreDataPointDataType.Phonenumber:
            case CoreDataPointDataType.Lookup:
                return [
                    { value: 'COUNT', label: 'Count' },
                ];

            default:
                return [];
        }
    };

    const availableAggregateOptions = getAvailableAggregateOptions(selectedFieldDataType);    
    
    const selectedAggregateIsValid = selectedCoreDataPointAggregate.trim().length === 0 ||
                                     availableAggregateOptions.some(option => option.value === selectedCoreDataPointAggregate);

    const hadExistingAggregate = (props.selectedCoreDataPointColumnAggregate?.aggregate?.trim().length ?? 0) > 0;

    const isRemovingAggregate = hadExistingAggregate && changeOccurred &&
                                selectedCoreDataPointAggregate.trim().length === 0;

    const hasValidAggregateConfiguration =
        (
            selectedCoreDataPointAggregateAlias.trim().length > 0 &&
            selectedCoreDataPointAggregate.trim().length > 0 &&
            selectedAggregateIsValid
        ) ||
        isRemovingAggregate;

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10',
        border: `1px solid black`,
        marginTop: 4,
        height: '95%'
    };

    return (
        <>
            <Row gutter={16} className='retainbi-compact-row'>
                <Col span={4} />
                <Col span={16}>
                    <div style={contentStyle}>
                        <h3 className='text-2xl pt-2 px-2'>Aggregation</h3>
                    </div>
                </Col>
                <Col span={4} />
            </Row>
            <Row gutter={16} className='retainbi-compact-row'>
                <Col span={1} />
                <Col span={22}>
                    <div>
                        <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                    </div>
                </Col>
                <Col span={1} />
            </Row>
            <Row gutter={16} className='retainbi-compact-row' style={{ backgroundColor: 'palegreen', borderRadius: '8px', }}>
                <Col span={1} />
                <Col span={11}>
                    <Text italic strong>Field:</Text>{" "}
                    <Text>{selectedFieldName}</Text>
                </Col>
                <Col span={11}>
                    <Text italic strong>Type:</Text>{" "}
                    <Text>{selectedTypeName}</Text>
                </Col>
                <Col span={1} />
            </Row>
            <Row gutter={16} className='retainbi-compact-row'>
                <Col span={24}>
                    <div>
                        <Divider style={{ margin: '1px' }}></Divider>
                    </div>
                </Col>
            </Row>
            <Row gutter={16} className='retainbi-compact-row'>
                <Col span={1} />
                <Col span={4}>
                    <div style={{ padding: '4px', textAlign: 'right' }}>
                        <Text>Column Alias:</Text>
                    </div>
                </Col>
                <Col span={6}>
                    <Input
                        allowClear={true}
                        value={selectedCoreDataPointAggregateAlias}
                        onChange={handleAggregateColumnAliasChange}
                    />
                </Col>
                <Col span={1} />
                <Col span={4}>
                    <div style={{ padding: '4px', textAlign: 'right' }}>
                        <Text>Aggregate:</Text>
                    </div>
                </Col>
                <Col span={6}>
                    <div style={{ paddingTop: '10px' }}>
                        <Select
                            showSearch
                            style={{ width: '100%' }}
                            value={selectedCoreDataPointAggregate}
                            onChange={(e: any) => handleAggregateChange(e)}
                        >
                            <Option key='not-set' value={''} label={'(Not Set)'}>
                                {'(Not Set)'}
                            </Option>
                            {
                                availableAggregateOptions.map(aggregateOption => (
                                    <Option
                                        key={aggregateOption.value}
                                        value={aggregateOption.value}
                                        label={aggregateOption.label}
                                    >
                                        {aggregateOption.label}
                                    </Option>
                                ))
                            }
                        </Select>
                    </div>
                </Col>
                <Col span={2} />
            </Row>
            <Row gutter={16} className='retainbi-compact-row'>
                <Col span={24}>
                    <div>
                        <Divider style={{ margin: '1px' }}></Divider>
                    </div>
                </Col>
            </Row>
            <Row gutter={16} className='retainbi-compact-row'>
                <Col span={15} />
                <Col span={4}>
                    <div style={{ textAlign: 'right' }}>
                        <Button
                            disabled={!changeOccurred || !hasValidAggregateConfiguration}
                            htmlType="button"
                            style={{width: '95%'}}
                            type='primary'
                            onClick={handleOk}
                        >
                            OK
                        </Button>
                    </div>
                </Col>
                <Col span={4}>
                    <div style={{ textAlign: 'right' }}>
                            <Button
                                style={{width: '95%'}}
                                type="default"
                                onClick={() => props.onCancel()}
                            >
                                Cancel
                            </Button>
                    </div>
                </Col>
                <Col span={1} />
            </Row>
        </>
    );
};

export default AHRBCoreDataPointAggregate;